/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro';
import { Tooltip } from '@grafana/ui';
import { DiversityType, Options, passwordStrength, Result } from 'check-password-strength';
import { useContext } from 'react';
import { Control, FieldValues, Path, useWatch } from 'react-hook-form';

import { StylingContext, StylingContextState } from 'context/Styling';
import { palette } from 'context/Styling/palette';
import { useStyles } from 'utils/useStyles';

const SEGMENT_COUNT = 4;

// Custom thresholds for check-password-strength. The library's defaults make
// id 2 (Medium) require all four character classes AND length 10-11 — a
// 2-character window — so most passwords jump from id 1 to id 3 and the
// third bar never appears. These thresholds keep Strong gated on all four
// classes + length 12, but let Medium be reached with three classes.
const STRENGTH_OPTIONS: Options<string> = [
  { id: 0, value: 'Too weak', minDiversity: 0, minLength: 0 },
  { id: 1, value: 'Weak', minDiversity: 2, minLength: 6 },
  { id: 2, value: 'Medium', minDiversity: 3, minLength: 8 },
  { id: 3, value: 'Strong', minDiversity: 4, minLength: 12 },
];

const DEFAULT_TIPS: Record<number, string> = {
  0: 'This password is extremely easy to guess. Add length, mixed case, numbers, and symbols.',
  1: 'This password is easy to guess. Try a longer phrase with a mix of characters.',
  2: 'Acceptable, but not great. Add more length or unusual characters to make it harder to guess.',
  3: 'Strong password. Hard to guess for most attackers.',
};

const MISSING_TIPS: Record<DiversityType, string> = {
  lowercase: 'Add lowercase letters.',
  uppercase: 'Add uppercase letters.',
  number: 'Add a number.',
  symbol: 'Add a symbol (e.g. !@#$).',
};

const getStyles = ({ colors }: StylingContextState) => ({
  wrapper: css`
    margin: -0.5rem 0 2rem 0;
  `,
  row: css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 8px;
  `,
  bar: css`
    display: flex;
    flex: 1 1 auto;
    flex-flow: row nowrap;
    gap: 4px;
  `,
  segment: css`
    flex: 1 1 0;
    height: 4px;
    border-radius: 2px;
    background-color: ${colors.gray9};
    transition: background-color 200ms ease;
  `,
  label: css`
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.gray11};
    min-width: 72px;
    text-align: right;
  `,
});

const colorForScore = (score: number, colors: StylingContextState['colors']) => {
  switch (score) {
    case 0:
      return colors.red3;
    case 1:
      return palette.orange;
    case 2:
      return palette.yellow;
    case 3:
    default:
      return colors.green;
  }
};

const buildTooltipContent = (result: Result<string>): string[] => {
  const tips: string[] = [];
  const allDiversity: DiversityType[] = ['lowercase', 'uppercase', 'number', 'symbol'];
  const missingClass = allDiversity.filter((className) => !result.contains.includes(className));

  for (const className of missingClass) {
    tips.push(MISSING_TIPS[className]);
  }

  if (tips.length === 0) {
    tips.push(DEFAULT_TIPS[result.id]);
  }

  return tips;
};

export interface PasswordStrengthProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  className?: string;
}

export function PasswordStrength<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  className,
}: PasswordStrengthProps<TFieldValues>) {
  const styles = useStyles((theme: StylingContextState) => getStyles(theme));
  const { colors } = useContext(StylingContext);
  const password: string = useWatch({ control, name }) ?? '';

  if (!password) {
    return null;
  }

  const result = passwordStrength(password, STRENGTH_OPTIONS);
  const filledCount = result.id + 1;
  const filledColor = colorForScore(result.id, colors);
  const tooltipContent = buildTooltipContent(result);

  const PasswordStrengthBar = (
    <div
      role="progressbar"
      aria-label="Password strength"
      aria-valuemin={0}
      aria-valuemax={SEGMENT_COUNT - 1}
      aria-valuenow={result.id}
      css={styles.bar}
    >
      {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
        <div
          key={index}
          css={styles.segment}
          style={index < filledCount ? { backgroundColor: filledColor } : undefined}
          data-testid={`password-strength-segment-${index}`}
          data-filled={index < filledCount ? 'true' : 'false'}
        />
      ))}
    </div>
  );

  const PasswordStrengthRow = (
    <div css={styles.row}>
      {PasswordStrengthBar}
      <span css={styles.label} data-testid="password-strength-label">
        {result.value}
      </span>
    </div>
  );

  return (
    <div className={className} css={styles.wrapper}>
      <Tooltip
        content={
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {tooltipContent.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        }
        placement="top"
      >
        {PasswordStrengthRow}
      </Tooltip>
    </div>
  );
}
